import React, { useMemo, useState, useEffect, useLayoutEffect, useCallback, useRef } from 'react';
import { Tag } from 'antd';
import { TrophyOutlined } from '@ant-design/icons';
import './TorneoBracket.css';

export const RONDAS_ORDEN = ['16VOS', '8VOS', '4TOS', 'SEMIS', 'FINAL'];

export const RONDA_LABELS_DEFAULT = {
  '16VOS': '16avos',
  '8VOS': 'Octavos',
  '4TOS': 'Cuartos',
  SEMIS: 'Semifinales',
  FINAL: 'Final',
};

const DEFAULT_CARD_HEIGHT = 92;
const MATCH_GAP = 28;
const ROUND_WIDTH = 300;
const ROUND_MARGIN = 40;
const CHAMPION_WIDTH = 184;

const TBD = 'A confirmar';

const normalizePorRonda = (porRonda) => {
  if (!porRonda) return {};
  const result = {};
  for (const [ronda, value] of Object.entries(porRonda)) {
    result[ronda] = Array.isArray(value) ? value : (value.partidos || []);
  }
  return result;
};

const normalizeMatch = (raw, ronda) => {
  const parejaA = raw.pareja_a ?? null;
  const parejaB = raw.pareja_b ?? null;
  const estado = raw.tpl_estado ?? (raw.resultado ? 'JUGADO' : 'PENDIENTE');
  const ganadorId = raw.tpl_ganador_inscripcion_id ?? null;
  const inscripcionAId = raw.tpl_inscripcion_a_id ?? null;
  const inscripcionBId = raw.tpl_inscripcion_b_id ?? null;
  const ganadorLabel = raw.ganador ?? null;

  let winnerIsA = false;
  let winnerIsB = false;
  if (estado === 'JUGADO' || estado === 'WO') {
    if (ganadorId && inscripcionAId) {
      winnerIsA = ganadorId === inscripcionAId;
      winnerIsB = ganadorId === inscripcionBId;
    } else if (ganadorLabel) {
      winnerIsA = ganadorLabel === parejaA;
      winnerIsB = ganadorLabel === parejaB;
    }
  }

  return {
    id: raw.tpl_id ?? raw.id ?? `${ronda}-${raw.tpl_orden ?? raw.orden ?? 0}`,
    ronda,
    orden: raw.tpl_orden ?? raw.orden ?? 0,
    parejaA: parejaA || TBD,
    parejaB: parejaB || TBD,
    isPlaceholderA: !parejaA,
    isPlaceholderB: !parejaB,
    estado,
    setsA: raw.tpl_sets_a ?? null,
    setsB: raw.tpl_sets_b ?? null,
    resultado: raw.resultado_formateado ?? raw.resultado ?? null,
    ganadorLabel,
    winnerIsA,
    winnerIsB,
    clasificacionA: raw.clasificacion_a ?? null,
    clasificacionB: raw.clasificacion_b ?? null,
    soyYo: Boolean(raw.soy_yo),
    clickable: Boolean(inscripcionAId && inscripcionBId),
    raw,
  };
};

const getTopPosition = (roundIndex, matchIndex, cardHeight, gap = MATCH_GAP) => {
  const unit = cardHeight + gap;
  const multiplier = 2 ** roundIndex;
  return matchIndex * multiplier * unit + ((multiplier - 1) * unit) / 2;
};

const getBracketHeight = (firstRoundCount, cardHeight, gap = MATCH_GAP) => {
  if (firstRoundCount <= 0) return cardHeight;
  return getTopPosition(0, firstRoundCount - 1, cardHeight, gap) + cardHeight;
};

/** Convierte cruces de preview en estructura de bracket con rondas futuras en TBD */
export const buildPreviewPorRonda = (cruces, primeraRonda) => {
  if (!cruces?.length || !primeraRonda) return null;
  const startIdx = RONDAS_ORDEN.indexOf(primeraRonda);
  if (startIdx < 0) return null;

  const porRonda = {};
  let matchCount = cruces.length;

  for (let r = startIdx; r < RONDAS_ORDEN.length; r += 1) {
    const ronda = RONDAS_ORDEN[r];
    if (r === startIdx) {
      porRonda[ronda] = cruces.map((c, i) => ({
        tpl_id: `preview-${i}`,
        tpl_orden: i + 1,
        pareja_a: c.etiqueta_a,
        pareja_b: c.es_bye && !c.etiqueta_b ? 'Bye' : c.etiqueta_b,
        clasificacion_a: c.clasificacion_a,
        clasificacion_b: c.clasificacion_b,
        tpl_estado: c.es_bye ? 'WO' : 'PENDIENTE',
        origen: c.origen,
      }));
    } else {
      porRonda[ronda] = Array.from({ length: matchCount }, (_, i) => ({
        tpl_id: `preview-${ronda}-${i}`,
        tpl_orden: i + 1,
        pareja_a: null,
        pareja_b: null,
        tpl_estado: 'PENDIENTE',
      }));
    }
    matchCount = Math.ceil(matchCount / 2);
    if (matchCount <= 0) break;
  }

  return { porRonda };
};

const BracketMatch = ({
  match,
  highlight,
  onClick,
  showDetail = true,
}) => {
  const isPlayed = match.estado === 'JUGADO' || match.estado === 'WO';
  const isWo = match.estado === 'WO';
  const isPlaceholder = match.isPlaceholderA && match.isPlaceholderB;

  const teamClass = (isWinner, isTbd, isLoser) => {
    const classes = ['torneo-bracket__team'];
    if (isWinner) classes.push('torneo-bracket__team--winner');
    else if (isLoser) classes.push('torneo-bracket__team--loser');
    if (isTbd) classes.push('torneo-bracket__team--tbd');
    return classes.join(' ');
  };

  const renderTeamRow = (pareja, clasificacion, isWinner, isPlaceholder, isLoser, sets, showWo) => (
    <div className={teamClass(isWinner, isPlaceholder, isLoser)}>
      <span className="torneo-bracket__team-name" title={pareja}>
        {pareja}
      </span>
      {clasificacion && (
        <span className="torneo-bracket__team-badge" title={`Posición en grupos: ${clasificacion}`}>
          {clasificacion}
        </span>
      )}
      {sets != null && (
        <span className="torneo-bracket__team-score">{sets}</span>
      )}
      {showWo && <Tag className="torneo-bracket__wo-tag" color="orange">WO</Tag>}
    </div>
  );

  return (
    <div
      className={[
        'torneo-bracket__match',
        highlight ? 'torneo-bracket__match--highlight' : '',
        onClick && match.clickable ? 'torneo-bracket__match--clickable' : '',
        isPlaceholder ? 'torneo-bracket__match--placeholder' : '',
      ].filter(Boolean).join(' ')}
      onClick={onClick && match.clickable ? () => onClick(match.raw) : undefined}
      role={onClick && match.clickable ? 'button' : undefined}
      tabIndex={onClick && match.clickable ? 0 : undefined}
      onKeyDown={
        onClick && match.clickable
          ? (e) => { if (e.key === 'Enter' || e.key === ' ') onClick(match.raw); }
          : undefined
      }
    >
      {renderTeamRow(
        match.parejaA,
        match.clasificacionA,
        match.winnerIsA,
        match.isPlaceholderA,
        isPlayed && !match.winnerIsA,
        isPlayed ? match.setsA : null,
        false,
      )}
      {renderTeamRow(
        match.parejaB,
        match.clasificacionB,
        match.winnerIsB,
        match.isPlaceholderB,
        isPlayed && !match.winnerIsB,
        isPlayed ? match.setsB : null,
        isWo,
      )}
      {showDetail && match.resultado && (
        <div className="torneo-bracket__result-detail">{match.resultado}</div>
      )}
    </div>
  );
};

const ConnectorLines = ({ measuredLayout, rounds, svgSize }) => {
  if (!measuredLayout?.length || !svgSize.width || !svgSize.height) return null;

  const paths = [];
  for (let r = 0; r < rounds.length - 1; r += 1) {
    const currentMatches = measuredLayout[r] || [];
    const nextMatches = measuredLayout[r + 1] || [];

    for (let m = 0; m < currentMatches.length; m += 1) {
      const from = currentMatches[m];
      const parentIndex = Math.floor(m / 2);
      const to = nextMatches[parentIndex];
      if (!from || !to) continue;

      const x1 = from.right;
      const y1 = from.centerY;
      const x2 = to.left;
      const y2 = to.centerY;
      const midX = x1 + (x2 - x1) / 2;

      paths.push(`M ${x1} ${y1} H ${midX} V ${y2} H ${x2}`);
    }
  }

  return (
    <svg
      className="torneo-bracket__svg-lines"
      width={svgSize.width}
      height={svgSize.height}
      aria-hidden
    >
      {paths.map((d, i) => (
        <path
          key={i}
          d={d}
          fill="none"
          stroke="#4a4a4a"
          strokeWidth="2"
          strokeLinejoin="round"
        />
      ))}
    </svg>
  );
};

const TorneoBracket = ({
  porRonda,
  rondaLabels = RONDA_LABELS_DEFAULT,
  onMatchClick,
  highlightMine = false,
  showChampion = true,
  showDetail = true,
  preview = false,
}) => {
  const bracketRef = useRef(null);
  const matchRefs = useRef({});
  const [measuredLayout, setMeasuredLayout] = useState([]);
  const [svgSize, setSvgSize] = useState({ width: 0, height: 0 });
  const [cardHeight, setCardHeight] = useState(DEFAULT_CARD_HEIGHT);

  const { rounds, normalized, champion, firstRoundCount } = useMemo(() => {
    const flat = normalizePorRonda(porRonda);
    const activeRounds = RONDAS_ORDEN.filter((r) => (flat[r]?.length ?? 0) > 0);
    const normalizedRounds = activeRounds.map((ronda) =>
      (flat[ronda] || [])
        .slice()
        .sort((a, b) => (a.tpl_orden ?? a.orden ?? 0) - (b.tpl_orden ?? b.orden ?? 0))
        .map((m) => normalizeMatch(m, ronda))
    );

    const firstCount = normalizedRounds[0]?.length ?? 1;

    let champ = null;
    const finalRound = normalizedRounds[normalizedRounds.length - 1];
    if (finalRound?.length === 1) {
      const final = finalRound[0];
      if (final.estado === 'JUGADO' || final.estado === 'WO') {
        champ = final.ganadorLabel
          || (final.winnerIsA ? final.parejaA : final.winnerIsB ? final.parejaB : null);
      }
    }

    return {
      rounds: activeRounds,
      normalized: normalizedRounds,
      firstRoundCount: firstCount,
      champion: champ,
    };
  }, [porRonda]);

  const bracketHeight = useMemo(
    () => getBracketHeight(firstRoundCount, cardHeight),
    [firstRoundCount, cardHeight]
  );

  useEffect(() => {
    setCardHeight(DEFAULT_CARD_HEIGHT);
  }, [porRonda]);

  const measureLayout = useCallback(() => {
    const root = bracketRef.current;
    if (!root || !rounds.length) return;

    const rootRect = root.getBoundingClientRect();
    const nextLayout = rounds.map((_, roundIndex) => {
      const matches = normalized[roundIndex] || [];
      return matches.map((match) => {
        const key = `${roundIndex}-${match.id}`;
        const el = matchRefs.current[key];
        if (!el) return null;
        const rect = el.getBoundingClientRect();
        return {
          left: rect.left - rootRect.left,
          right: rect.right - rootRect.left,
          top: rect.top - rootRect.top,
          centerY: rect.top - rootRect.top + rect.height / 2,
        };
      });
    });

    const firstRoundMatches = normalized[0] || [];
    let maxMeasuredHeight = 0;
    for (const match of firstRoundMatches) {
      const el = matchRefs.current[`0-${match.id}`];
      if (el) maxMeasuredHeight = Math.max(maxMeasuredHeight, el.offsetHeight);
    }
    if (maxMeasuredHeight > 0) {
      const paddedHeight = maxMeasuredHeight + 4;
      setCardHeight((prev) => (Math.abs(prev - paddedHeight) > 1 ? paddedHeight : prev));
    }

    setMeasuredLayout(nextLayout);
    setSvgSize({
      width: root.offsetWidth,
      height: root.offsetHeight,
    });
  }, [rounds, normalized]);

  useLayoutEffect(() => {
    measureLayout();
  }, [measureLayout, cardHeight, bracketHeight]);

  useEffect(() => {
    const root = bracketRef.current;
    if (!root) return undefined;

    const observer = new ResizeObserver(() => {
      measureLayout();
    });
    observer.observe(root);

    const onResize = () => measureLayout();
    window.addEventListener('resize', onResize);

    return () => {
      observer.disconnect();
      window.removeEventListener('resize', onResize);
    };
  }, [measureLayout, bracketHeight, showDetail, champion]);

  if (!rounds.length) return null;

  const totalWidth = rounds.length * (ROUND_WIDTH + ROUND_MARGIN)
    + (showChampion && champion ? CHAMPION_WIDTH : 0);
  const hasChampionBlock = showChampion && champion;

  return (
    <div className="torneo-bracket-wrap">
      <div
        ref={bracketRef}
        className="torneo-bracket"
        style={{
          minHeight: bracketHeight + 40,
          minWidth: totalWidth,
        }}
      >
        <ConnectorLines
          measuredLayout={measuredLayout}
          rounds={rounds}
          svgSize={svgSize}
        />

        {rounds.map((ronda, roundIndex) => (
          <div key={ronda} className="torneo-bracket__round" style={{ width: ROUND_WIDTH }}>
            <div className="torneo-bracket__round-title">
              {rondaLabels[ronda] || RONDA_LABELS_DEFAULT[ronda] || ronda}
              {preview && roundIndex > 0 && (
                <span style={{ opacity: 0.6, fontWeight: 400 }}> (preview)</span>
              )}
            </div>
            <div
              className="torneo-bracket__round-matches"
              style={{ height: bracketHeight }}
            >
              {(normalized[roundIndex] || []).map((match, matchIndex) => (
                <div
                  key={match.id}
                  ref={(el) => {
                    matchRefs.current[`${roundIndex}-${match.id}`] = el;
                  }}
                  className="torneo-bracket__match-slot"
                  style={{ top: getTopPosition(roundIndex, matchIndex, cardHeight) }}
                >
                  <BracketMatch
                    match={match}
                    highlight={highlightMine && match.soyYo}
                    onClick={onMatchClick}
                    showDetail={showDetail}
                  />
                </div>
              ))}
            </div>
          </div>
        ))}

        {hasChampionBlock && (
          <div className="torneo-bracket__champion">
            <TrophyOutlined className="torneo-bracket__champion-trophy" />
            <span className="torneo-bracket__champion-label">Campeón</span>
            <span className="torneo-bracket__champion-name">{champion}</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default TorneoBracket;
